import type { ReactNode } from "react";
import { Outlet, useLocation } from "@tanstack/react-router";
import { CommandMenu } from "@shared/components/command-menu";
import { useVisualViewportInset } from "@shared/hooks/use-visual-viewport-inset";
import { cn } from "@shared/lib/utils";
import {
  TooltipProvider,
  useCloseTooltipsOnRouteChange,
} from "@shared/ui/tooltip";
import { MobileHeader, type MobileHeaderProps } from "./MobileHeader";
import { BottomNav } from "./BottomNav";

export interface MobileShellProps extends MobileHeaderProps {
  children?: ReactNode;
  offlineBanner?: ReactNode;
  installPromptBanner?: ReactNode;
  /**
   * Full-bleed routes (`navFullBleed`, e.g. /health-assistant) own their own
   * scrolling. The shell then becomes a fixed-height (`h-dvh`) column and
   * `main` an unpadded, non-scrolling `flex-1 min-h-0` box that ends above the
   * bottom nav, so a child sized `h-full` can pin its own composer and scroll
   * its own message list. Every other route keeps the page-scrolls layout.
   */
  fullBleed?: boolean;
}

/** Bottom nav row height (BottomNav tabs are `min-h-[56px]`). */
export const BOTTOM_NAV_HEIGHT_PX = 56;
/**
 * Content clearance above the fixed tab bar. `--bottom-nav-h` is published by
 * `useBottomInset` (measured, safe-area excluded) so this follows the bar when
 * it resizes or the keyboard hides it; the constant is the fallback for pages
 * that never mount that hook.
 */
const BOTTOM_NAV_HEIGHT = `var(--bottom-nav-h, ${BOTTOM_NAV_HEIGHT_PX}px)`;

/**
 * MobileShell — full-screen layout for viewport < 768px.
 *
 * Structure (top → bottom):
 *   sticky MobileHeader (with safe-area-inset-top via CSS)
 *   OfflineBanner (if offline / queued)
 *   InstallPromptBanner (if PWA-installable)
 *   scrollable content (Outlet or children)
 *   fixed BottomNav (with safe-area-inset-bottom)
 *
 * The header is the app bar: its title is a plain <span>, not a heading, so it
 * never competes with the page's own <h1>. A page that wants a second row
 * (conversation title, actions) renders one compact bar inside its content.
 *
 * While the on-screen keyboard is open the BottomNav is hidden (it would sit on
 * top of the keyboard and bury a pinned composer) and `--kb-inset` is
 * published for pages that need to lift fixed elements (iOS Safari).
 */
export function MobileShell({
  title,
  rightAction,
  secondaryNavContent,
  children,
  offlineBanner,
  installPromptBanner,
  fullBleed = false,
}: MobileShellProps) {
  const { pathname } = useLocation();
  // Tooltips never outlive the page they were opened on.
  useCloseTooltipsOnRouteChange(pathname);
  const { keyboardOpen } = useVisualViewportInset();

  return (
    <TooltipProvider>
      {/* The header overflow menu's "Search" opens this palette. */}
      <CommandMenu />
      <div
        className={cn(
          "flex flex-col bg-background text-foreground",
          // h-dvh: the *dynamic* viewport, so browser chrome / the keyboard do not
          // push the composer off-screen (100vh fallback lives in globals.css).
          fullBleed ? "h-dvh overflow-hidden" : "min-h-screen",
        )}
        data-testid="mobile-shell"
        data-full-bleed={fullBleed ? "true" : undefined}
      >
        <MobileHeader
          title={title}
          rightAction={rightAction}
          secondaryNavContent={secondaryNavContent}
        />

        {offlineBanner}
        {installPromptBanner}

        <main
          id="main-content"
          tabIndex={-1}
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            fullBleed ? "overflow-hidden" : "overflow-y-auto",
          )}
          style={
            fullBleed
              ? {
                  // No padding: the page fills the box edge to edge. The bottom
                  // offset keeps its last row (the composer) above the fixed nav.
                  paddingBottom: keyboardOpen
                    ? 0
                    : `calc(${BOTTOM_NAV_HEIGHT} + env(safe-area-inset-bottom))`,
                }
              : {
                  paddingInlineStart: "calc(1rem + env(safe-area-inset-left))",
                  paddingInlineEnd: "calc(1rem + env(safe-area-inset-right))",
                  paddingTop: "1rem",
                  paddingBottom: `calc(${BOTTOM_NAV_HEIGHT} + env(safe-area-inset-bottom) + 1rem)`,
                }
          }
        >
          {children ?? <Outlet />}
        </main>

        <BottomNav hidden={keyboardOpen} />
      </div>
    </TooltipProvider>
  );
}
